import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, DollarSign, CheckCircle2, Users, Handshake } from 'lucide-react';
import { clsx } from 'clsx';
import type { Property, Contact } from '../../types/types';
import { useCRM } from '../../contexts/CRMContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabase';

// Cross-agency colleague option for the "Colaboración" counterpart picker
// (shape from the get_network_colleagues RPC — bypasses profiles RLS).
interface ColleagueOption {
  profile_id: string;
  full_name: string;
  agency_name: string | null;
}

interface RegistrarVentaModalProps {
  property: Property;
  /** The buyer. When omitted, the modal shows a contact picker to connect one. */
  contact?: Contact | null;
  onClose: () => void;
  /** Called after a sale is recorded; the host supplies a refresh of the lists. */
  onRecorded?: () => void;
}

function todayISO(): string {
  // Local calendar day — toISOString() is UTC, which is already tomorrow after
  // 18:00 in Guatemala.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const RegistrarVentaModal: React.FC<RegistrarVentaModalProps> = ({
  property,
  contact,
  onClose,
  onRecorded,
}) => {
  const { theme } = useTheme();
  const isLight = theme !== 'dark';
  const { leads, owners } = useCRM();
  const { profile } = useAuth();

  // Cross-agency (Intercambio) close: the property belongs to another agency,
  // so the caller records THEIR sale but gets no property side effects — the
  // RPC enforces the same rule server-side. `sharedBy` is set on shared-hub
  // cards; agency_id covers rows hydrated by direct fetch.
  const isExternal = property.sharedBy != null
    || (property.agency_id != null && profile?.agency_id != null
        && String(property.agency_id) !== String(profile.agency_id));

  const [buyerId, setBuyerId] = useState<string>(contact?.id ? String(contact.id) : '');
  const [buyerName, setBuyerName] = useState<string>(contact?.name || '');
  const [buyerSearch, setBuyerSearch] = useState('');
  // Counterpart mode: own lead vs. cross-agency collaboration (Intercambio).
  // Both are optional — a sale can be recorded with no counterpart at all.
  const [counterpartMode, setCounterpartMode] = useState<'lead' | 'colega'>('lead');
  const [collabId, setCollabId] = useState<string>('');
  const [collabName, setCollabName] = useState<string>('');
  const [collabSearch, setCollabSearch] = useState('');
  const [colleagues, setColleagues] = useState<ColleagueOption[]>([]);
  const [colleaguesLoaded, setColleaguesLoaded] = useState(false);
  const [sellPrice, setSellPrice] = useState<string>(property.price != null ? String(property.price) : '');
  const [commissionPct, setCommissionPct] = useState<string>(
    contact?.expected_commission != null ? String(contact.expected_commission) : ''
  );
  const [sellDate, setSellDate] = useState<string>(todayISO());
  const isPublished = property.publish_status === 'published';
  const isShared = property.share_status === 'shared';
  const [markSold, setMarkSold] = useState(true);
  const [unlist, setUnlist] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buyerLocked = !!contact?.id;

  const allContacts = useMemo(() => [...leads, ...owners], [leads, owners]);
  const filteredContacts = useMemo(() => {
    if (!buyerSearch) return allContacts.slice(0, 8);
    const q = buyerSearch.toLowerCase();
    return allContacts.filter(c => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [allContacts, buyerSearch]);

  // Lazy-load the cross-agency colleagues list the first time the user
  // switches to Colaboración (profiles RLS blocks direct selects, so this
  // goes through the SECURITY DEFINER network RPC used by /colegas).
  useEffect(() => {
    if (counterpartMode !== 'colega' || colleaguesLoaded || !supabase) return;
    supabase.rpc('get_network_colleagues').then(({ data, error: rpcError }) => {
      if (rpcError) {
        console.error('get_network_colleagues failed:', rpcError);
        return;
      }
      setColleagues(
        ((data ?? []) as any[]).map(p => ({
          profile_id: String(p.profile_id),
          full_name: p.full_name || 'Agente',
          agency_name: p.agency_name || null,
        }))
      );
      setColleaguesLoaded(true);
    });
  }, [counterpartMode, colleaguesLoaded]);

  const filteredColleagues = useMemo(() => {
    if (!collabSearch) return colleagues.slice(0, 8);
    const q = collabSearch.toLowerCase();
    return colleagues
      .filter(c => c.full_name.toLowerCase().includes(q) || (c.agency_name ?? '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [colleagues, collabSearch]);

  const switchCounterpartMode = (mode: 'lead' | 'colega') => {
    if (mode === counterpartMode) return;
    setCounterpartMode(mode);
    // Mutually exclusive in the UI: switching modes drops the other selection.
    if (mode === 'lead') { setCollabId(''); setCollabName(''); setCollabSearch(''); }
    else { setBuyerId(''); setBuyerName(''); setBuyerSearch(''); }
  };

  const bg = isLight ? 'bg-white' : 'bg-zinc-900';
  const border = isLight ? 'border-zinc-200' : 'border-zinc-700';
  const text = isLight ? 'text-zinc-900' : 'text-zinc-100';
  const textMuted = isLight ? 'text-zinc-500' : 'text-zinc-400';
  const inputBg = isLight ? 'bg-zinc-50 border-zinc-200' : 'bg-zinc-800 border-zinc-700';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const priceNum = sellPrice.trim() ? Number(sellPrice) : null;
    if (priceNum != null && (Number.isNaN(priceNum) || priceNum < 0)) {
      setError('El precio de venta no es válido.');
      return;
    }
    const commNum = commissionPct.trim() ? Number(commissionPct) : null;
    if (commNum != null && (Number.isNaN(commNum) || commNum < 0 || commNum > 100)) {
      setError('La comisión debe estar entre 0 y 100.');
      return;
    }
    setIsSaving(true);
    try {
      const { error: rpcError } = await supabase!.rpc('record_property_sale', {
        p_property_id: property.id,
        p_contact_id: buyerId || null,
        p_collaborator_profile_id: collabId || null,
        p_sell_price: priceNum,
        p_commission_pct: commNum,
        p_sell_date: sellDate || todayISO(),
        p_mark_property_sold: isExternal ? false : markSold,
        p_unlist: isExternal ? false : unlist,
      });
      if (rpcError) throw rpcError;
      onRecorded?.();
      onClose();
    } catch (err: any) {
      console.error('record_property_sale failed:', err);
      setError(err?.message || 'No se pudo registrar la venta.');
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className={clsx('relative w-full max-w-md max-h-[90vh] rounded-xl border shadow-2xl flex flex-col', bg, border, text)}>
        {/* Header */}
        <div className={clsx('flex items-center justify-between p-4 border-b shrink-0', border)}>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500">
              <DollarSign size={18} />
            </div>
            <h2 className="text-lg font-semibold">Registrar Venta</h2>
          </div>
          <button onClick={onClose} className={clsx('p-1 rounded-lg hover:bg-zinc-500/20', textMuted)}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable body — keeps header and the Cancelar/Registrar actions pinned */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Property context */}
          <div className={clsx('px-3 py-2 rounded-lg border text-sm', inputBg)}>
            <p className="font-medium truncate">{property.title || 'Propiedad'}</p>
            {property.ii_id && <p className={clsx('text-xs', textMuted)}>{property.ii_id}</p>}
          </div>

          {/* Counterpart — optional: own lead OR a cross-agency colleague
              (collaboration). Both pickers clear when switching modes. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={clsx('block text-sm font-medium', textMuted)}>
                Comprador <span className="font-normal">(opcional)</span>
              </label>
              {!buyerLocked && (
                <div className={clsx('flex rounded-lg border overflow-hidden', border)}>
                  <button
                    type="button"
                    onClick={() => switchCounterpartMode('lead')}
                    className={clsx(
                      'flex items-center gap-1 px-2 py-1 text-xs font-medium transition-colors',
                      counterpartMode === 'lead'
                        ? 'bg-emerald-600 text-white'
                        : clsx(textMuted, 'hover:bg-zinc-500/10')
                    )}
                  >
                    <Users size={12} /> Lead
                  </button>
                  <button
                    type="button"
                    onClick={() => switchCounterpartMode('colega')}
                    className={clsx(
                      'flex items-center gap-1 px-2 py-1 text-xs font-medium transition-colors',
                      counterpartMode === 'colega'
                        ? 'bg-emerald-600 text-white'
                        : clsx(textMuted, 'hover:bg-zinc-500/10')
                    )}
                  >
                    <Handshake size={12} /> Colaboración
                  </button>
                </div>
              )}
            </div>

            {buyerLocked ? (
              <div className={clsx('px-3 py-2 rounded-lg border text-sm', inputBg, text)}>{buyerName}</div>
            ) : counterpartMode === 'lead' ? (
              buyerId ? (
                <div className={clsx('flex items-center justify-between px-3 py-2 rounded-lg border text-sm', inputBg, text)}>
                  <span>{buyerName}</span>
                  <button
                    type="button"
                    onClick={() => { setBuyerId(''); setBuyerName(''); setBuyerSearch(''); }}
                    className={clsx('text-xs', textMuted)}
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search size={14} className={clsx('absolute left-3 top-2.5', textMuted)} />
                    <input
                      type="text"
                      value={buyerSearch}
                      onChange={e => setBuyerSearch(e.target.value)}
                      placeholder="Buscar contacto... (opcional)"
                      className={clsx('w-full pl-8 pr-3 py-2 rounded-lg border text-sm', inputBg, text)}
                    />
                  </div>
                  {filteredContacts.length > 0 && (
                    <div className={clsx('mt-1 max-h-32 overflow-y-auto rounded-lg border', inputBg, border)}>
                      {filteredContacts.map(c => (
                        <button
                          key={String(c.id)}
                          type="button"
                          onClick={() => { setBuyerId(String(c.id)); setBuyerName(c.name); setBuyerSearch(''); }}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-500/20"
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )
            ) : collabId ? (
              <div className={clsx('flex items-center justify-between px-3 py-2 rounded-lg border text-sm', inputBg, text)}>
                <span className="truncate">{collabName}</span>
                <button
                  type="button"
                  onClick={() => { setCollabId(''); setCollabName(''); setCollabSearch(''); }}
                  className={clsx('text-xs shrink-0 ml-2', textMuted)}
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={14} className={clsx('absolute left-3 top-2.5', textMuted)} />
                  <input
                    type="text"
                    value={collabSearch}
                    onChange={e => setCollabSearch(e.target.value)}
                    placeholder="Buscar colega de otra agencia..."
                    className={clsx('w-full pl-8 pr-3 py-2 rounded-lg border text-sm', inputBg, text)}
                  />
                </div>
                {!colleaguesLoaded ? (
                  <p className={clsx('mt-1 text-xs', textMuted)}>Cargando colegas de la red...</p>
                ) : filteredColleagues.length > 0 ? (
                  <div className={clsx('mt-1 max-h-32 overflow-y-auto rounded-lg border', inputBg, border)}>
                    {filteredColleagues.map(c => {
                      const display = c.agency_name ? `${c.full_name} — ${c.agency_name}` : c.full_name;
                      return (
                        <button
                          key={c.profile_id}
                          type="button"
                          onClick={() => { setCollabId(c.profile_id); setCollabName(display); setCollabSearch(''); }}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-500/20"
                        >
                          <span className="block truncate">{display}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className={clsx('mt-1 text-xs', textMuted)}>Sin colegas que coincidan.</p>
                )}
              </>
            )}
          </div>

          {/* Price + Commission */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={clsx('block text-sm font-medium mb-1', textMuted)}>Precio de venta</label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                value={sellPrice}
                onChange={e => setSellPrice(e.target.value)}
                placeholder="0"
                className={clsx('w-full px-3 py-2 rounded-lg border text-sm', inputBg, text)}
              />
            </div>
            <div>
              <label className={clsx('block text-sm font-medium mb-1', textMuted)}>% Comisión</label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                max="100"
                step="0.1"
                value={commissionPct}
                onChange={e => setCommissionPct(e.target.value)}
                placeholder="0"
                className={clsx('w-full px-3 py-2 rounded-lg border text-sm', inputBg, text)}
              />
            </div>
          </div>

          {/* Sale date */}
          <div>
            <label className={clsx('block text-sm font-medium mb-1', textMuted)}>Fecha de venta</label>
            <input
              type="date"
              value={sellDate}
              onChange={e => setSellDate(e.target.value)}
              className={clsx('w-full px-3 py-2 rounded-lg border text-sm', inputBg, text)}
            />
          </div>

          {/* Side effects — only the owning agency controls the property's
              status/visibility. Cross-agency (Intercambio) closes record the
              sale without touching the listing. */}
          {isExternal ? (
            <p className={clsx('text-xs pt-1', textMuted)}>
              Propiedad de otra agencia: su estado y publicación los administra la agencia propietaria.
            </p>
          ) : (
          <div className="space-y-2 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={markSold} onChange={e => setMarkSold(e.target.checked)} className="accent-emerald-600" />
              <span>Marcar la propiedad como Vendida</span>
            </label>
            {(isPublished || isShared) && (
              <label className={clsx('flex items-center gap-2 text-sm cursor-pointer', !markSold && 'opacity-40')}>
                <input
                  type="checkbox"
                  checked={unlist && markSold}
                  disabled={!markSold}
                  onChange={e => setUnlist(e.target.checked)}
                  className="accent-emerald-600"
                />
                <span>Dejar de compartir y publicar</span>
              </label>
            )}
          </div>
          )}

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
          </div>

          {/* Actions */}
          <div className={clsx('flex items-center gap-2 p-4 border-t shrink-0', border)}>
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className={clsx('px-4 py-2 text-sm rounded-lg hover:bg-zinc-500/20', textMuted)}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg"
            >
              <CheckCircle2 size={15} />
              {isSaving ? 'Registrando...' : 'Registrar Venta'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
